import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class BuniqueSetComponent extends Component {
  requiredRefs = ['variantSelects', 'submitButton', 'status', 'total', 'totalPrice'];
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.handleSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.handleSelection();
  }

  #selection() {
    return Array.from(this.refs.variantSelects ?? []).map((select) => {
      const option = select.selectedOptions[0];
      return select.value && option?.dataset.available === 'true' && !option.disabled
        ? { id: select.value, quantity: 1, price: Number(option.dataset.price), select }
        : null;
    });
  }

  handleSelection() {
    const selected = this.#selection();
    const ready = selected.length === 2 && selected.every(Boolean);
    this.refs.submitButton.disabled = this.#busy || !ready;
    this.refs.submitButton.textContent = this.#busy
      ? this.dataset.loadingMessage
      : ready ? this.dataset.readyMessage : this.dataset.selectMessage;
    this.refs.total.hidden = !ready;
    if (ready) {
      const total = selected.reduce((sum, item) => sum + item.price, 0);
      this.refs.totalPrice.textContent = this.#money(total);
    }
    for (const item of selected) {
      if (item) item.select.closest('fieldset').querySelector('[data-piece-price]').textContent = this.#money(item.price);
    }
    this.refs.status.textContent = '';
  }

  #money(cents) {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
  }

  async #cart() {
    const component = document.querySelector('cart-items-component');
    if (component) {
      await customElements.whenDefined('cart-items-component');
      return component.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    return response.json();
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;
    const selected = this.#selection();
    if (selected.length !== 2 || selected.some((item) => !item)) {
      this.handleSelection();
      this.refs.status.textContent = this.dataset.selectMessage;
      this.refs.variantSelects.find((select) => !select.value)?.focus();
      return;
    }
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;
    this.#busy = true;
    this.handleSelection();
    this.setAttribute('aria-busy', 'true');
    const pending = CartLinesUpdateEvent.createPromise();
    const sectionIds = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map((item) => item.dataset.sectionId).filter(Boolean))];
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: selected.map((item) => ({ merchandiseId: item.id, quantity: 1 })),
      promise: pending.promise,
    }));
    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: selected.map((item) => ({ id: Number(item.id), quantity: 1 })), sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = typeof data.description === 'string' ? data.description : this.dataset.errorMessage;
        throw new Error(message);
      }
      added = true;
      const cart = await this.#cart();
      pending.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'bunique-set', sourceId: this.id, itemCount: 2, productId: this.dataset.productId, sections: data.sections, didError: false },
      });
      this.refs.status.textContent = this.dataset.successMessage;
    } catch (error) {
      try {
        const cart = await this.#cart();
        pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'bunique-set', sourceId: this.id, didError: true } });
      } catch (refreshError) {
        pending.reject(refreshError);
      }
      const message = added ? this.dataset.successMessage : error.message || this.dataset.errorMessage;
      this.refs.status.textContent = message;
      if (!added) this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      const message = this.refs.status.textContent;
      this.handleSelection();
      this.refs.status.textContent = message;
    }
  }
}

if (!customElements.get('bunique-set-component')) customElements.define('bunique-set-component', BuniqueSetComponent);
